// Siege engines from rigid parts (2026-10-25): no skeleton. scripts/pack_siege.ts names the parts (frame, body,
// wheel_l, wheel_r, mount, crew_l, crew_r); this moves them. The wheels roll with the distance travelled, the bed rocks
// a little on the move, and the weapon on the mount fires: a recoil kick and a bolt that flies out and reloads. The
// Samurai great bow is built here (fal's content checker refused its images for Meshy), the bolt for every army.
// Frame space: +x is forward (the tow beam), y up, the wheels' radius is the frame's R.
// 2026-10-26, one rig for every army (SiegeKind): the Samurai great bow, the Knights trebuchet and the Greek stone
// thrower ride the same cart (the r26 cart and wheel, packed per army), their engines built here as the bow is; the
// Viking battering ram has no cart: a log carried by four crew, all built here (ramRoot), the crew surging with it.
// 2026-10-26, armies2: the Mongol wagon catapult and the Chinese rocket cart ride the same cart; the Egyptian siege
// shed (a roofed ram on four wheels, pushed from behind) and the Aztec slinger band (three slingers as one unit, no
// engine) are made whole here, as the ram is. CARTLESS lists the engines with no file.
import * as THREE from 'three';
import { clone as skClone } from 'three/examples/jsm/utils/SkeletonUtils.js';

const wood = new THREE.MeshStandardMaterial({ color: '#7a5230', roughness: 0.55 });
const lacquer = new THREE.MeshStandardMaterial({ color: '#1d1f26', roughness: 0.35 });
const cord = new THREE.MeshStandardMaterial({ color: '#f1ede4', roughness: 0.8 });
const steel = new THREE.MeshStandardMaterial({ color: '#cfd5da', roughness: 0.3, metalness: 0.5 });
/** The sheet's team blue, so teamtint.ts repaints it with the rest of the unit (premium.ts paints shields with it). */
export const trim = new THREE.MeshStandardMaterial({ color: '#1f3f9e', roughness: 0.4 });

/** The oyumi: a long stock on a short post, a wide bow across the front, a cord to the nut. */
function greatBow() {
  const g = new THREE.Group();
  const post = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.16, 0.12), wood); post.position.y = 0.08;
  const stock = new THREE.Mesh(new THREE.BoxGeometry(1.15, 0.09, 0.12), wood); stock.position.set(0.1, 0.2, 0);
  const band = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.1, 0.13), trim); band.position.set(-0.3, 0.2, 0);
  const lac = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.1, 0.13), lacquer); lac.position.set(0.45, 0.2, 0);
  const arc = new THREE.QuadraticBezierCurve3(new THREE.Vector3(0.42, 0.2, -0.62), new THREE.Vector3(0.78, 0.2, 0), new THREE.Vector3(0.42, 0.2, 0.62));
  const bow = new THREE.Mesh(new THREE.TubeGeometry(arc, 24, 0.03, 6), wood);
  const nut = new THREE.Vector3(-0.05, 0.22, 0);
  const strings = [-0.62, 0.62].map((z) => {
    const a = new THREE.Vector3(0.42, 0.2, z), m = new THREE.Mesh(new THREE.CylinderGeometry(0.008, 0.008, a.distanceTo(nut), 4), cord);
    m.position.copy(a).add(nut).multiplyScalar(0.5);
    m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), nut.clone().sub(a).normalize());
    return m;
  });
  g.add(post, stock, band, lac, bow, ...strings);
  return g;
}

const iron = new THREE.MeshStandardMaterial({ color: '#4a4d52', roughness: 0.35, metalness: 0.4 });
const stoneMat = new THREE.MeshStandardMaterial({ color: '#b9b2a6', roughness: 0.7 });
const bark = new THREE.MeshStandardMaterial({ color: '#8a5f36', roughness: 0.6 });

export type SiegeKind = 'bow' | 'trebuchet' | 'stone' | 'ram' | 'catapult' | 'rockets' | 'shed' | 'slingers';
/** The engines made whole in code, no cart file to load (siegeRoot builds them). */
export const CARTLESS: readonly SiegeKind[] = ['ram', 'shed', 'slingers'];
const felt = new THREE.MeshStandardMaterial({ color: '#e8dcc0', roughness: 0.85 });
const hide = new THREE.MeshStandardMaterial({ color: '#a9895f', roughness: 0.8 });

/** A beam from a to b, its cross-section w square. */
function beam(a: THREE.Vector3, b: THREE.Vector3, w: number, mat: THREE.Material) {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, a.distanceTo(b), w), mat);
  m.position.copy(a).add(b).multiplyScalar(0.5);
  m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), b.clone().sub(a).normalize());
  return m;
}
const V = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z);

/** The Knights trebuchet: two A-frames on the bed, an iron axle at their apex, and a throwing arm whose short end
 *  carries the counterweight box (team paint, the engine's one big team area) and whose long end the stone. The arm is
 *  a child named 'arm' that turns about the axle (its z); at rest it is cocked, the long end down at the back. */
function trebuchet() {
  const g = new THREE.Group(), A = 1.05;
  for (const z of [-0.26, 0.26]) g.add(beam(V(-0.42, 0, z), V(0, A, z), 0.07, wood), beam(V(0.42, 0, z), V(0, A, z), 0.07, wood), beam(V(-0.3, 0.3, z), V(0.3, 0.3, z), 0.05, wood));
  const axle = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.035, 0.66, 8).rotateX(Math.PI / 2), iron); axle.position.y = A;
  const arm = new THREE.Group(); arm.name = 'arm'; arm.position.y = A;
  arm.add(beam(V(0.34, 0, 0), V(-1.15, 0, 0), 0.055, wood));
  const box = new THREE.Mesh(new THREE.BoxGeometry(0.26, 0.24, 0.3), trim); box.position.set(0.34, -0.16, 0);
  const band = new THREE.Mesh(new THREE.BoxGeometry(0.27, 0.04, 0.31), iron); band.position.set(0.34, -0.06, 0);
  const shot = new THREE.Mesh(new THREE.SphereGeometry(0.09, 10, 8), stoneMat); shot.position.set(-1.2, -0.06, 0); shot.name = 'stone';
  arm.add(box, band, shot);
  g.add(axle, arm);
  return g;
}

/** The Greek stone thrower: a low frame on the bed with a twisted rope spring across it, one arm through the spring
 *  with a cup at its end, and a padded crossbar on two posts that stops the arm. The arm turns about the spring (its
 *  z); at rest it lies back. The side rails and the bar's pad are team paint. */
function stoneThrower() {
  const g = new THREE.Group(), P = V(-0.18, 0.2, 0);
  for (const z of [-0.22, 0.22]) g.add(beam(V(-0.5, 0.06, z), V(0.45, 0.06, z), 0.08, trim), beam(V(-0.18, 0.06, z), V(-0.18, 0.28, z), 0.07, wood), beam(V(-0.03, 0.06, z), V(-0.03, 0.74, z), 0.06, wood));
  const spring = new THREE.Mesh(new THREE.CylinderGeometry(0.075, 0.075, 0.42, 10).rotateX(Math.PI / 2), cord); spring.position.copy(P);
  const bar = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.07, 0.5), wood); bar.position.set(-0.03, 0.74, 0);
  const pad = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.1, 0.26), trim); pad.position.set(-0.07, 0.74, 0);
  const arm = new THREE.Group(); arm.name = 'arm'; arm.position.copy(P);
  arm.add(beam(V(0, 0, 0), V(0, 0.85, 0), 0.05, wood));
  const cup = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.06, 0.07, 10), wood); cup.position.y = 0.86;
  const shot = new THREE.Mesh(new THREE.SphereGeometry(0.08, 10, 8), stoneMat); shot.position.y = 0.92; shot.name = 'stone';
  arm.add(cup, shot);
  g.add(spring, bar, pad, arm);
  return g;
}

/** The Viking ram: a long log with a dark iron cap and bands, painted in the team color between the carriers' bands
 *  (the engine's team area, as the counterweight is the trebuchet's). Along +x. */
function ramLog() {
  const g = new THREE.Group(), L = 2.3;
  g.add(new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.11, L, 10).rotateZ(Math.PI / 2), bark));
  const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.125, 0.22, 10).rotateZ(-Math.PI / 2), iron); cap.position.x = L / 2 + 0.06;
  const nose = new THREE.Mesh(new THREE.SphereGeometry(0.09, 10, 6), iron); nose.position.x = L / 2 + 0.17;
  g.add(cap, nose);
  for (const x of [-0.75, -0.2, 0.35, 0.85]) { const b = new THREE.Mesh(new THREE.CylinderGeometry(0.118, 0.118, 0.05, 10).rotateZ(Math.PI / 2), iron); b.position.x = x; g.add(b); }
  for (const x of [-0.475, 0.075, 0.6]) { const p = new THREE.Mesh(new THREE.CylinderGeometry(0.112, 0.112, 0.4, 10).rotateZ(Math.PI / 2), trim); p.position.x = x; g.add(p); }
  return g;
}

/** The Mongol wagon catapult: a small swing-beam thrower, two felt-wrapped posts and a crossbar that stops the arm, a
 *  team-paint felt apron hung across the front. The arm turns about the posts' axle (its z); at rest it lies back. */
function catapult() {
  const g = new THREE.Group(), P = V(-0.12, 0.42, 0);
  for (const z of [-0.2, 0.2]) g.add(beam(V(-0.12, 0, z), V(-0.12, 0.5, z), 0.07, felt), beam(V(-0.45, 0.04, z), V(0.35, 0.04, z), 0.07, wood), beam(V(0.06, 0.04, z), V(0.06, 0.62, z), 0.05, wood));
  const axle = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.46, 8).rotateX(Math.PI / 2), iron); axle.position.copy(P);
  const bar = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.06, 0.46), wood); bar.position.set(0.06, 0.62, 0);
  const apron = new THREE.Mesh(new THREE.BoxGeometry(0.03, 0.34, 0.5), trim); apron.position.set(0.37, 0.24, 0);
  const arm = new THREE.Group(); arm.name = 'arm'; arm.position.copy(P);
  arm.add(beam(V(0, -0.12, 0), V(0, 0.62, 0), 0.045, wood));
  const cup = new THREE.Mesh(new THREE.SphereGeometry(0.075, 10, 6, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2), hide); cup.position.y = 0.66;
  const shot = new THREE.Mesh(new THREE.SphereGeometry(0.065, 10, 8), stoneMat); shot.position.y = 0.68; shot.name = 'stone';
  arm.add(cup, shot);
  g.add(axle, bar, apron, arm);
  return g;
}

/** The Chinese rocket cart: a box of fire arrows on a short post, its sides team paint, the rockets' iron heads in a
 *  grid on its front. Each rocket is a node named 'rocket' that flies out in the volley. */
function rocketBox() {
  const g = new THREE.Group(), y = 0.42;
  const post = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.26, 0.12), wood); post.position.y = 0.13;
  const box = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.34, 0.5), trim); box.position.set(-0.05, y, 0); box.rotation.z = 0.18;
  const lid = new THREE.Mesh(new THREE.BoxGeometry(0.64, 0.04, 0.52), lacquer); lid.position.set(-0.02, y + 0.19, 0); lid.rotation.z = 0.18;
  g.add(post, box, lid);
  for (let i = 0; i < 4; i++) for (let j = 0; j < 3; j++) {
    const r = new THREE.Group(); r.name = 'rocket';
    const tube = new THREE.Mesh(new THREE.CylinderGeometry(0.018, 0.018, 0.34, 6).rotateZ(-Math.PI / 2), cord);
    const tip = new THREE.Mesh(new THREE.ConeGeometry(0.03, 0.08, 5).rotateZ(-Math.PI / 2), iron); tip.position.x = 0.2;
    r.add(tube, tip);
    r.position.set(0.12 + 0.05 * j, y - 0.1 + 0.1 * j, -0.18 + 0.12 * i); r.rotation.z = 0.18;
    g.add(r);
  }
  return g;
}

/** A spoked wheel of radius r, its axle along z (it rolls by its rotation.z): a rim, six spokes and a hub. */
export function spokedWheel(r: number, rimMat: THREE.Material = wood) {
  const g = new THREE.Group();
  g.add(new THREE.Mesh(new THREE.TorusGeometry(r * 0.94, r * 0.07, 6, 20), rimMat));
  for (let k = 0; k < 6; k++) { const s = new THREE.Mesh(new THREE.BoxGeometry(r * 0.08, r * 1.8, r * 0.06), wood); s.rotation.z = (k * Math.PI) / 6; g.add(s); }
  g.add(new THREE.Mesh(new THREE.CylinderGeometry(r * 0.16, r * 0.16, r * 0.3, 8).rotateX(Math.PI / 2), iron));
  return g;
}

/** The engine's crew nodes: [name, x, z, turn], x forward along the frame. */
type Anchor = readonly [string, number, number, number];
function anchors(mount: THREE.Object3D, list: readonly Anchor[]) {
  for (const [name, x, z, turn] of list) { const o = new THREE.Object3D(); o.name = name; o.position.set(x, 0, z); o.rotation.y = turn; mount.add(o); }
}
/** siege > frame > mount, the frame's forward (+x) the game's (+z), as pack_siege.ts turns a cart. */
function engineRoot() {
  const siege = new THREE.Group(), frame = new THREE.Group(), mount = new THREE.Group();
  siege.name = 'siege'; frame.name = 'frame'; mount.name = 'mount';
  siege.add(frame); frame.add(mount);
  frame.rotation.y = -Math.PI / 2;
  return { siege, frame, mount };
}

/** The Egyptian siege shed: a long hide-walled shed with a peaked roof in team paint on four spoked wheels, the ram
 *  log slung inside (its nose out the front), and two crew pushing from behind. Its wheels are the frame's wheel_ nodes. */
function shedRoot() {
  const { siege, frame, mount } = engineRoot(), R = 0.2, L = 1.9, W = 0.86, H = 0.78, A = 1.08;
  const body = new THREE.Group(); body.name = 'body';
  for (const x of [-L / 2, L / 2]) for (const z of [-W / 2, W / 2]) body.add(beam(V(x, R, z), V(x, H, z), 0.06, wood));
  for (const z of [-W / 2, W / 2]) {
    const wall = new THREE.Mesh(new THREE.BoxGeometry(L, H - R - 0.12, 0.03), hide); wall.position.set(0, (H + R + 0.12) / 2, z); body.add(wall);
    body.add(beam(V(-L / 2, H, z), V(L / 2, H, z), 0.06, wood));
  }
  const slope = Math.hypot(W / 2 + 0.08, A - H);
  for (const s of [-1, 1]) {
    const roof = new THREE.Mesh(new THREE.BoxGeometry(L + 0.16, 0.04, slope), trim);
    roof.position.set(0, (H + A) / 2, (s * (W / 2 + 0.08)) / 2); roof.rotation.x = s * Math.atan2(A - H, W / 2 + 0.08);
    body.add(roof);
  }
  body.add(beam(V(-L / 2 - 0.08, A, 0), V(L / 2 + 0.08, A, 0), 0.05, wood));
  frame.add(body);
  for (const [name, x, z] of [['wheel_l', 0.6, W / 2 + 0.05], ['wheel_r', 0.6, -W / 2 - 0.05], ['wheel_l2', -0.6, W / 2 + 0.05], ['wheel_r2', -0.6, -W / 2 - 0.05]] as const) {
    const w = spokedWheel(R); w.name = name; w.position.set(x, R, z); frame.add(w);
  }
  mount.position.y = 0.5;
  anchors(frame, [['crew_l', -L / 2 - 0.4, 0.24, 0], ['crew_r', -L / 2 - 0.4, -0.24, 0]]);
  return siege;
}

/** The Aztec slinger band: no engine, three slingers in a wedge, the front one in the middle. Each has a stone
 *  ('stone' nodes on the mount) that he lobs in the volley. */
function slingersRoot() {
  const { siege, mount } = engineRoot();
  anchors(mount, [['crew_c', 0.42, 0, 0], ['crew_l', -0.3, 0.46, -0.2], ['crew_r', -0.3, -0.46, 0.2]]);
  for (const [x, z] of [[0.42, 0], [-0.3, 0.46], [-0.3, -0.46]]) {
    const s = new THREE.Mesh(new THREE.SphereGeometry(0.06, 8, 6), stoneMat); s.name = 'stone'; s.position.set(x + 0.1, 1.15, z); mount.add(s);
  }
  return siege;
}

/** The root of an engine with no file (CARTLESS). */
export function siegeRoot(kind: SiegeKind) {
  if (kind === 'shed') return shedRoot();
  if (kind === 'slingers') return slingersRoot();
  if (kind === 'ram') return ramRoot();
  throw new Error(`the ${kind} rides a cart file`);
}

/** A Viking ram's whole engine, made here (no file): siege > frame > mount > the log and four crew nodes, two each
 *  side, a little turned in toward the log. The log's height is set to the crew's hands when they mount. */
export function ramRoot() {
  const siege = new THREE.Group(), frame = new THREE.Group(), mount = new THREE.Group();
  siege.name = 'siege'; frame.name = 'frame'; mount.name = 'mount';
  siege.add(frame); frame.add(mount);
  frame.rotation.y = -Math.PI / 2; // the frame's forward (+x) is the game's (+z), as pack_siege.ts turns a cart
  for (const [name, x, z] of [['crew_l', 0.45, 0.32], ['crew_r', 0.45, -0.32], ['crew_l2', -0.45, 0.32], ['crew_r2', -0.45, -0.32]] as const) {
    const o = new THREE.Object3D(); o.name = name; o.position.set(x, 0, z); o.rotation.y = z > 0 ? -0.35 : 0.35; mount.add(o);
  }
  return siege;
}
const TWO = ['crew_l', 'crew_r'];
const CREW: Record<SiegeKind, string[]> = { bow: TWO, trebuchet: TWO, stone: TWO, catapult: TWO, rockets: TWO, shed: TWO, ram: ['crew_l', 'crew_r', 'crew_l2', 'crew_r2'], slingers: ['crew_c', 'crew_l', 'crew_r'] };

function bolt() {
  const g = new THREE.Group();
  const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.018, 0.018, 0.85, 6), wood); shaft.rotation.z = Math.PI / 2;
  const head = new THREE.Mesh(new THREE.ConeGeometry(0.04, 0.12, 4), steel); head.rotation.z = -Math.PI / 2; head.position.x = 0.48;
  const fletch = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.08, 0.004), cord); fletch.position.x = -0.36;
  g.add(shaft, head, fletch);
  return g;
}

export class SiegeRig {
  readonly frame: THREE.Object3D;
  private wheels: THREE.Object3D[] = [];
  private bow = new THREE.Group();
  private shot: THREE.Object3D = bolt();
  /** The trebuchet's, the stone thrower's and the catapult's throwing arm; the ram's mount (log and crew surge
   *  together); the shed's log (it surges inside the shed, the crew stay). */
  private arm: THREE.Object3D | null = null;
  /** The rocket cart's rockets and the slingers' stones: each flies out in the volley from where it rests. */
  private volley: { o: THREE.Object3D; home: THREE.Vector3; dir: THREE.Vector3; at: number }[] = [];
  private R: number;
  private roll = 0;
  private kick = 0;
  /** Seconds since the last shot; the bolt is in flight for the first FLY of them. */
  private since = 99;
  private crew: { root: THREE.Object3D; mixer: THREE.AnimationMixer; walk?: THREE.AnimationAction; idle?: THREE.AnimationAction; swing?: THREE.AnimationAction }[] = [];
  static FLY = 0.45;
  /** How tall the crew stand, in frame units: the engine's scale against its soldiers. */
  static CREW_H = 1.25;

  constructor(root: THREE.Object3D, readonly kind: SiegeKind = 'bow') {
    const get = (n: string) => { const o = root.getObjectByName(n); if (!o) throw new Error(`siege file has no ${n} node`); return o; };
    this.frame = get('frame');
    const mount = get('mount');
    this.frame.traverse((o) => { if (o.name.startsWith('wheel_')) this.wheels.push(o); });
    if (kind === 'ram' || kind === 'slingers') this.R = 1; // no wheels; R only scales the roll
    else {
      if (this.wheels.length < 2) throw new Error('siege file has no wheel_ nodes');
      this.R = this.frame.position.y + this.wheels[0].position.y; // the frame is lifted by R minus the axle's height
    }
    if (kind === 'ram') this.arm = mount;
    // a clone of a rigged engine already carries its weapon: drive that one instead of adding a second
    const have = mount.getObjectByName('weapon');
    if (have) {
      this.bow = have as THREE.Group; this.shot = have.getObjectByName(kind === 'bow' ? 'bolt' : 'stone') ?? this.shot;
      if (kind === 'shed') this.arm = have;
      else if (kind !== 'ram') this.arm = have.getObjectByName('arm') ?? null;
      this.aimVolley(mount);
      return;
    }
    this.bow.name = 'weapon';
    mount.add(this.bow);
    if (kind === 'bow') {
      this.shot.name = 'bolt';
      this.bow.add(greatBow());
      this.shot.position.set(0.15, 0.27, 0);
      this.bow.add(this.shot);
      return;
    }
    if (kind === 'slingers') { this.aimVolley(mount); return; }
    const engine = { trebuchet, stone: stoneThrower, catapult, rockets: rocketBox, ram: ramLog, shed: ramLog }[kind]();
    this.bow.add(engine);
    if (kind === 'ram') return;
    if (kind === 'shed') { this.arm = this.bow; return; }
    if (kind === 'rockets') { this.aimVolley(mount); return; }
    this.arm = engine.getObjectByName('arm')!; this.shot = engine.getObjectByName('stone')!;
  }

  /** The rockets fan out a little, each a beat after the last; the slingers' stones go up and over, one each. */
  private aimVolley(mount: THREE.Object3D) {
    if (this.kind !== 'rockets' && this.kind !== 'slingers') return;
    const shots: THREE.Object3D[] = [];
    mount.traverse((o) => { if (o.name === (this.kind === 'rockets' ? 'rocket' : 'stone')) shots.push(o); });
    this.volley = shots.map((o, i) => ({
      o, home: o.position.clone(), // the slingers throw as one (their clip is the same, Astra F4), so their stones leave together
      at: this.kind === 'rockets' ? i * 0.035 : SiegeRig.SLING,
      dir: this.kind === 'rockets' ? V(1, 0.25 + 0.04 * ((i % 3) - 1), 0.12 * ((Math.floor(i / 3) - 1.5))) : V(1, 0, 0.1 * o.position.z),
    }));
  }

  /** Two crewmen (a rigged unit of their own) at the crew_l and crew_r nodes, facing forward. On a clone of an engine
   *  that already has its crew, call it again with the same clips: it drives the cloned crew rather than adding more. */
  mountCrew(crew: THREE.Object3D, clips: THREE.AnimationClip[], height = SiegeRig.CREW_H) {
    const box = new THREE.Box3().setFromObject(crew), s = height / box.getSize(new THREE.Vector3()).y;
    for (const name of CREW[this.kind]) {
      const anchor = this.frame.getObjectByName(name)!;
      let c = anchor.getObjectByName('crew');
      if (!c) {
        c = skClone(crew); c.name = 'crew'; c.scale.setScalar(s); c.rotation.y = Math.PI / 2; // the crew face +z, the frame's forward is +x
        anchor.add(c);
      }
      const mixer = new THREE.AnimationMixer(c), clip = (n: string) => clips.find((x) => x.name === n);
      const walk = clip('walk') && mixer.clipAction(clip('walk')!), idle = clip('idle') && mixer.clipAction(clip('idle')!);
      // the slingers throw on the attack: their own clip, once a shot
      const swing = this.kind === 'slingers' && clip('attack') ? mixer.clipAction(clip('attack')!).setLoop(THREE.LoopOnce, 1) : undefined;
      if (swing) swing.clampWhenFinished = true;
      (walk ?? idle)?.play();
      this.crew.push({ root: c, mixer, walk, idle, swing });
    }
    if (this.kind === 'ram') this.fitLog();
  }

  /** The ram's log at the carriers' hands, as they hold it on the move (their walk is the charge, its grip). */
  private fitLog() {
    const c = this.crew[0];
    (c.walk ?? c.idle)?.play(); c.mixer.setTime(0);
    this.frame.updateMatrixWorld(true);
    const hands = ['RightHand', 'LeftHand'].map((n) => c.root.getObjectByName(n)).filter(Boolean) as THREE.Object3D[];
    if (!hands.length) return;
    this.bow.position.y = hands.reduce((n, h) => n + this.bow.parent!.worldToLocal(h.getWorldPosition(new THREE.Vector3())).y, 0) / hands.length;
    this.hips = c.root.getObjectByName('Hips') ?? null;
    this.logRise = this.bow.position.y - this.hipsY();
  }
  private hips: THREE.Object3D | null = null;
  private logRise = 0;
  private hipsY() { return this.bow.parent!.worldToLocal(this.hips!.getWorldPosition(new THREE.Vector3())).y; }
  /** The log rides the carriers' hips as they step (it stood still while they bobbed under it; their hands pump too
   *  much on the run to hang it from them). */
  private carry() {
    if (!this.hips) return;
    this.frame.updateMatrixWorld(true);
    this.bow.position.y = this.hipsY() + this.logRise;
  }

  fire() { this.since = 0; this.kick = 1; }

  /** speed: ground speed in frame units a second (0 standing). */
  update(dt: number, speed: number) {
    this.roll += (speed * dt) / this.R;
    this.kick = Math.max(0, this.kick - dt * 5);
    this.since += dt;
    this.apply(speed > 0);
    const go = !!speed || this.surging(), throwing = !speed && this.throwing();
    for (const c of this.crew) {
      if (throwing && c.swing && this.since <= dt) c.swing.reset().play();
      c.swing?.setEffectiveWeight(throwing ? 1 : 0);
      c.walk?.setEffectiveWeight(go ? 1 : 0); c.idle?.setEffectiveWeight(go || throwing ? 0 : 1);
      if (go && c.walk && !c.walk.isRunning()) c.walk.play();
      if (!go && c.idle && !c.idle.isRunning()) c.idle.play();
      c.mixer.update(dt);
    }
    this.carry();
  }

  /** The ram's carriers run with it on the strike. */
  private surging() { return this.kind === 'ram' && this.since < 0.9; }
  /** The slingers are mid-throw. */
  private throwing() { return !!this.crew[0]?.swing && this.since < this.crew[0].swing.getClip().duration; }

  /** The engine at time t of a move, from nothing but t (the match bakes these, baked.ts). walk: rolling at `speed`,
   *  one wheel turn every 2 pi R / speed seconds; attack: standing, the shot leaves at 0; idle: standing. */
  pose(move: string, t: number, speed = 0.9) {
    const moving = move === 'walk';
    this.roll = moving ? (speed * t) / this.R : 0;
    this.since = move === 'attack' ? t : 99;
    this.kick = move === 'attack' ? Math.max(0, 1 - t * 5) : 0;
    this.apply(moving);
    const throwing = move === 'attack' && !!this.crew[0]?.swing, go = !throwing && (moving || this.surging() || !this.crew[0]?.idle);
    for (const c of this.crew) {
      for (const a of [c.walk, c.idle]) a?.play();
      c.swing?.reset().play(); // a finished throw stays clamped, so it starts over
      c.swing?.setEffectiveWeight(throwing ? 1 : 0);
      c.walk?.setEffectiveWeight(go ? 1 : 0); c.idle?.setEffectiveWeight(go || throwing ? 0 : 1);
      c.mixer.setTime(t);
    }
    this.carry();
  }

  /** Seconds a crewman's walk takes, so a baked roll can loop with it. */
  get crewStride() { return this.crew[0]?.walk?.getClip().duration ?? 1; }

  get radius() { return this.R; }

  private apply(moving: boolean) {
    for (const w of this.wheels) w.rotation.z = -this.roll; // the wheel's axle is its own z
    if (this.wheels.length) this.frame.rotation.x = moving ? Math.sin(this.roll * 4) * 0.015 : 0; // the bed rocks with the wheels' bumps
    const s = this.since;
    if (this.kind === 'bow') {
      this.bow.position.x = -0.08 * Math.sin(this.kick * Math.PI);
      const k = s / SiegeRig.FLY;
      this.shot.visible = k >= 1.6 || k < 1; // gone for a moment after it lands, then reloaded
      this.shot.position.x = k < 1 ? 0.15 + k * 6 : 0.15;
    } else if (this.kind === 'ram' || this.kind === 'shed') {
      // a step back, the drive (the log a third of a body past where it was carried), then walked back
      this.arm!.position.x = s < 0.25 ? -0.12 * ease(s / 0.25) : s < 0.4 ? -0.12 + 0.44 * ease((s - 0.25) / 0.15) : s < 0.9 ? 0.32 * (1 - ease((s - 0.4) / 0.5)) : 0;
    } else if (this.volley.length) {
      if (this.kind === 'rockets') this.bow.position.x = -0.05 * Math.sin(this.kick * Math.PI);
      // a rocket flies straight and fast; a slinger's stone goes up and comes down; both are back by the reload
      for (const v of this.volley) {
        const k = (s - v.at) / SiegeRig.FLY;
        v.o.visible = k < 0 || k < 1 || s >= SiegeRig.RELOAD;
        const f = k > 0 && k < 1 ? k : 0;
        v.o.position.copy(v.home).addScaledVector(v.dir, f * 6);
        if (this.kind === 'slingers') v.o.position.y += 2.4 * f * (1 - f);
      }
    } else {
      this.arm!.rotation.z = armAngle(this.kind, s);
      this.shot.visible = s < SiegeRig.RELEASE || s >= SiegeRig.RELOAD; // the stone leaves at the top of the swing
    }
  }
  /** Seconds into a throw when the stone leaves the arm, and when the crew have it cocked and loaded again. */
  static RELEASE = 0.28;
  static RELOAD = 1.6;
  /** Seconds into the slingers' throw when the first stone leaves the sling. */
  static SLING = 0.35;
}

const ease = (x: number) => { const u = Math.min(1, Math.max(0, x)); return u * u * (3 - 2 * u); };

/** The throwing arm's turn about its pivot (z), s seconds into a throw (99 is cocked, at rest). The trebuchet's long end
 *  starts down at the back and swings up over the axle; the stone thrower's arm lies back and snaps up onto its bar,
 *  rebounds, and is cranked down again. */
export function armAngle(kind: SiegeKind, s: number) {
  const [rest, top] = kind === 'trebuchet' ? [0.95, -1.95] : kind === 'catapult' ? [1.35, -0.3] : [1.25, -0.12];
  if (s < 0.3) return rest + (top - rest) * (s / 0.3) ** 2;
  if (kind !== 'trebuchet' && s < 0.45) return top + 0.25 * Math.sin(((s - 0.3) / 0.15) * Math.PI);
  if (s < 0.5) return top;
  return top + (rest - top) * ease((s - 0.5) / (SiegeRig.RELOAD - 0.6));
}
